import type { MessageId } from '@/shared/i18n/catalogue.en'
import { CAPABILITIES, hasCapability } from '@/shared/session/capabilities'
import type { Session } from '@/shared/session/types'
import type { IconName } from '@/shared/ui/icons'

export const SCHEDULE_SECTIONS = [
  'working-calendar',
  'availability',
  'resources',
  'visit-types',
] as const

export type ScheduleSection = 'calendar' | (typeof SCHEDULE_SECTIONS)[number]

export function isScheduleSection(
  value: unknown,
): value is Exclude<ScheduleSection, 'calendar'> {
  return (
    typeof value === 'string' &&
    (SCHEDULE_SECTIONS as readonly string[]).includes(value)
  )
}

export const SCHEDULE_NAV_ITEMS: readonly {
  id: ScheduleSection
  label: MessageId
  description: MessageId
  icon: IconName
}[] = [
  {
    id: 'calendar',
    label: 'schedule.nav.calendar',
    description: 'schedule.nav.calendarDescription',
    icon: 'calendar',
  },
  {
    id: 'working-calendar',
    label: 'schedule.nav.workingCalendar',
    description: 'schedule.nav.working-calendarDescription',
    // Each section its own picture: the rail folds the words away, and the
    // three plain calendars this column used to draw were one tile thrice.
    icon: 'calendar-clock',
  },
  {
    id: 'availability',
    label: 'schedule.nav.availability',
    description: 'schedule.nav.availabilityDescription',
    icon: 'calendar-check',
  },
  {
    id: 'resources',
    label: 'schedule.nav.resources',
    description: 'schedule.nav.resourcesDescription',
    // Places, organizations, external resources: a pin on a map, not the
    // gear, which is the Control Center's door.
    icon: 'map-pin',
  },
  {
    id: 'visit-types',
    label: 'schedule.nav.visitTypes',
    description: 'schedule.nav.visit-typesDescription',
    // A visit to a clinician, as a kind.
    icon: 'stethoscope',
  },
]

export function scheduleSectionCapability(section: ScheduleSection) {
  if (section === 'calendar') return CAPABILITIES.scheduleRead
  if (section === 'working-calendar') return CAPABILITIES.workingCalendarsRead
  if (section === 'availability') return CAPABILITIES.availabilityRead
  if (section === 'resources') return CAPABILITIES.resourcesRead
  return CAPABILITIES.visitTypesRead
}

export function defaultScheduleSection(session: Session | undefined): ScheduleSection | null {
  return (
    SCHEDULE_NAV_ITEMS.find((item) =>
      hasCapability(session, scheduleSectionCapability(item.id)),
    )?.id ?? null
  )
}
